import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import SEO from '../../components/SEO/SEO'
import { seoBreadcrumbPair } from '../../utils/seoUtils'
import './Founder.css'

function Founder() {
  const { t } = useTranslation()

  const breadcrumbItems = useMemo(
    () => seoBreadcrumbPair(t('common.home'), t('common.founder'), 'founder'),
    [t]
  )

  return (
    <div className="founder-page">
      <SEO 
        title={`${t('founder.name', 'Pavel Gavrilovich Bessonov')} - ${t('common.founder')}`}
        description={t('founder.description', 'Learn about the founder of SUMA International - Pavel Gavrilovich Bessonov')}
        breadcrumbItems={breadcrumbItems}
      />
      <div className="container">
        <div className="founder-hero">
          <div className="founder-hero-content">
            <div className="founder-photo-container">
              <img 
                src="/images/persons/Grandmaster_Bessonov_Pavel_Gavrilovich.jpg" 
                alt={t('founder.name', 'Pavel Gavrilovich Bessonov')}
                className="founder-photo"
              />
            </div>
            <div className="founder-hero-text">
              <p className="founder-subtitle">{t('common.founder')}</p>
              <h1>{t('founder.name', 'Pavel Gavrilovich Bessonov')}</h1>
              <p className="founder-dates">{t('founder.dates', '(05.09.1949 - 05.03.2025)')}</p>
              <p className="founder-intro">
                {t('founder.intro', 'Pavel Gavrilovich Bessonov (05.09.1949 - 05.03.2025) was the founder and creator of SUMA International (International School Union of Martial Arts), a distinguished master of martial arts who dedicated his life to the development and promotion of martial arts worldwide.')}
              </p>
            </div>
          </div>
        </div>

        <div className="founder-content">
          <section className="founder-section">
            <h2>{t('founder.biography', 'Biography')}</h2>
            <p>
              {t('founder.biographyText', 'Pavel Gavrilovich Bessonov was a distinguished master of martial arts with decades of experience in training, teaching, and promoting various disciplines. Having studied multiple martial arts systems, Pavel Gavrilovich recognized the need for a unified organization that would preserve traditional values while fostering innovation and international cooperation.')}
            </p>
            <p>
              {t('founder.biographyTextKUBO', 'Pavel Gavrilovich Bessonov was the creator of KUBO (Classical-Universal Combat System), a unique martial arts system that combined elements of classical wrestling, jujutsu, and traditional Japanese martial arts. This system became the foundation for the development of SUMA International.')}
            </p>
            <p>
              {t('founder.biographyText2', 'Pavel Gavrilovich actively contributed to the development of martial arts, organizing competitions, seminars, and various events. Under his leadership, martial arts festivals and other significant events were held that united martial arts practitioners.')}
            </p>
            <p>
              {t('founder.biographyText3', 'Pavel Gavrilovich\'s contribution to the development of martial arts left a significant mark. His dedication, pedagogical talent, and organizational abilities allowed him to create a strong community of martial arts practitioners that continues to develop.')}
            </p>
          </section>

          <section className="founder-section">
            <h2>{t('founder.training', 'Activities and Achievements')}</h2>
            <div className="training-timeline">
              <div className="timeline-item">
                <h3>{t('founder.training1', 'Training and Teaching')}</h3>
                <p>{t('founder.training1Text', 'Pavel Gavrilovich began martial arts training at a young age, studying multiple disciplines including classical wrestling, jujutsu, and traditional Japanese martial arts. Continued advanced training under renowned masters, achieving high ranks in several disciplines.')}</p>
              </div>
              <div className="timeline-item">
                <h3>{t('founder.training2', 'Event Organization')}</h3>
                <p>{t('founder.training2Text', 'Actively organized competitions, seminars, and martial arts festivals. These events contributed to the popularization of martial arts and united practitioners of various disciplines.')}</p>
              </div>
              <div className="timeline-item">
                <h3>{t('founder.training3', 'Development of Martial Arts')}</h3>
                <p>{t('founder.training3Text', 'Made a significant contribution to the development of martial arts. His work in creating SUMA International allowed him to unite martial arts schools and federations at the international level, creating a platform for cooperation, certification, and development.')}</p>
              </div>
            </div>
          </section>

          <section className="founder-section">
            <h2>{t('founder.achievements', 'Achievements')}</h2>
            <div className="achievements-list">
              <div className="achievement-item">
                <h3>{t('founder.achievement1', 'Martial Arts Master')}</h3>
                <p>{t('founder.achievement1Text', 'Recognition as a master in multiple martial arts disciplines, holding high ranks in various martial arts systems.')}</p>
              </div>
              <div className="achievement-item">
                <h3>{t('founder.achievementKUBO', 'Creator of KUBO')}</h3>
                <p>{t('founder.achievementKUBOText', 'Created KUBO (Classical-Universal Combat System) - a unique martial arts system that combined elements of classical wrestling, jujutsu, and traditional Japanese martial arts. This system became the foundation for SUMA International.')}</p>
              </div>
              <div className="achievement-item">
                <h3>{t('founder.achievement2', 'Founder of SUMA International')}</h3>
                <p>{t('founder.achievement2Text', 'Created SUMA International (International School Union of Martial Arts) to unite martial arts schools and federations worldwide, creating a platform for cooperation, certification, and development.')}</p>
              </div>
              <div className="achievement-item">
                <h3>{t('founder.achievement3', 'Event Organizer')}</h3>
                <p>{t('founder.achievement3Text', 'Organized numerous competitions, seminars, and martial arts festivals, contributing to the popularization and development of martial arts.')}</p>
              </div>
              <div className="achievement-item">
                <h3>{t('founder.achievement4', 'Author and Educator')}</h3>
                <p>{t('founder.achievement4Text', 'Published books and educational materials on martial arts, sharing knowledge and techniques with practitioners around the world.')}</p>
              </div>
              <div className="achievement-item">
                <h3>{t('founder.achievement5', 'Mentor and Educator')}</h3>
                <p>{t('founder.achievement5Text', 'Raised many students and martial arts practitioners, passing on knowledge, traditions, and the philosophy of martial arts to future generations.')}</p>
              </div>
            </div>
          </section>

          <section className="founder-section">
            <h2>{t('founder.philosophy', 'Philosophy')}</h2>
            <p>
              {t('founder.philosophyText', 'The founder\'s philosophy emphasizes respect, discipline, and the continuous pursuit of excellence in martial arts practice. Central to this philosophy is the belief that martial arts are not just physical techniques, but a way of life that develops character, promotes harmony, and preserves cultural heritage.')}
            </p>
            <div className="philosophy-principles">
              <div className="principle-item">
                <h3>{t('founder.principle1', 'Respect')}</h3>
                <p>{t('founder.principle1Text', 'Respect for tradition, teachers, fellow practitioners, and oneself is fundamental to martial arts practice.')}</p>
              </div>
              <div className="principle-item">
                <h3>{t('founder.principle2', 'Discipline')}</h3>
                <p>{t('founder.principle2Text', 'Discipline in training, thought, and action leads to mastery and personal growth.')}</p>
              </div>
              <div className="principle-item">
                <h3>{t('founder.principle3', 'Excellence')}</h3>
                <p>{t('founder.principle3Text', 'Continuous pursuit of excellence in technique, knowledge, and character development.')}</p>
              </div>
              <div className="principle-item">
                <h3>{t('founder.principle4', 'Unity')}</h3>
                <p>{t('founder.principle4Text', 'Bringing together diverse martial arts communities while preserving their unique traditions and identities.')}</p>
              </div>
            </div>
          </section>

          <section className="founder-section">
            <h2>{t('founder.vision', 'Vision for SUMA International')}</h2>
            <p>
              {t('founder.visionText', 'Pavel Gavrilovich envisioned SUMA International as a global organization that would unite martial arts schools and federations, promote excellence in training, preserve traditional values, and foster international cooperation. The goal is to create a platform where practitioners can share knowledge, participate in certification programs, and contribute to the development of martial arts worldwide.')}
            </p>
          </section>
        </div>
      </div>
    </div>
  )
}

export default Founder
